<template>
  <div class="faq-page">
    <div class="faq-page__header">
      <h1 class="faq-page__title">Frequently Asked Questions</h1>
      <p class="faq-page__subtitle">Everything you need to know about Thinking of U</p>
    </div>

    <div class="faq-page__card">
      <div
        class="faq-item"
        v-for="(faq, index) in faqs"
        :key="index"
        :class="{ 'faq-item--open': expanded[index] }"
      >
        <button
          class="faq-question"
          @click="toggleFaq(index)"
          :aria-expanded="expanded[index]"
          :aria-controls="`faq-answer-${index}`"
        >
          <span>{{ faq.question }}</span>
          <span class="toggle-icon">{{ expanded[index] ? '−' : '+' }}</span>
        </button>

        <transition name="slide">
          <div
            v-show="expanded[index]"
            class="faq-answer"
            :id="`faq-answer-${index}`"
            role="region"
            :aria-labelledby="`faq-question-${index}`"
            v-html="faq.answer"
          />
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import { useHead } from '@vueuse/head';

const FAQS = [
        {
          question: 'What is Thinking of U?',
          answer: 'Thinking of U is a surprise-first postcard service that helps you send something meaningful — not because you wrote every word, but because you took the time to send a vibe. Our AI crafts creative, one-of-a-kind messages and images based on your choices.',
        },
        {
          question: 'How does it work?',
          answer: 'You choose a few key details: your relationship to the recipient, the tone (or vibe), the message style, and optionally, an occasion. Then our AI takes over and delivers something surprising, heartfelt, and uniquely yours.',
        },
        {
          question: 'Can I preview or edit the message?',
          answer: "Nope — and that's the point. Thinking of U is about trust and surprise. You pick the vibe, and we handle the rest. No previews, no edits — just a perfectly unique little surprise.",
        },
        {
          question: 'What if I want to send more than one postcard?',
          answer: "Go for it! Each card is its own experience. You can send as many postcards as you'd like — each uniquely tailored to its recipient.",
        },
        {
          question: 'Is my information secure?',
          answer: "Absolutely. We take privacy seriously and use secure, encrypted systems to process your data. We'll never share your personal info without your consent.",
        },
        {
          question: 'How much does it cost?',
          answer: 'Each postcard is just $5.',
        },
        {
          question: 'How do I get in touch with support?',
          answer: "We're happy to help! Email <a href='mailto:support@thinking-of-u.com'>support@thinking-of-u.com</a> or <a href='/contact-us'>contact us</a> anytime.",
        },
        {
          question: 'How long does delivery take?',
          answer: 'Most postcards arrive in 5\u20137 business days, depending on the destination. International shipping may vary slightly.',
        },
        {
          question: "What if I don't like what was generated?",
          answer: "We totally get it — surprises don't always hit perfectly. That said, most people find the charm in the unexpected. If something feels off, let us know and we'll make it right: <a href='/contact-us'>contact us</a>.",
        },
];

// FAQPage structured data so the Q&A can earn rich results (and is machine-
// readable even before client render). HTML stripped from answers for schema text.
function faqSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.answer.replace(/<[^>]*>/g, '').trim(),
      },
    })),
  };
}

export default {
  name: 'FAQ',
  setup() {
    useHead({
      script: [{ type: 'application/ld+json', children: JSON.stringify(faqSchema()) }],
    });
  },
  data() {
    return {
      faqs: FAQS,
      expanded: FAQS.map(() => false),
    };
  },
  methods: {
    toggleFaq(index) {
      this.expanded[index] = !this.expanded[index];
    },
  },
};
</script>

<style scoped>
.faq-page {
  background: linear-gradient(180deg, var(--light-pink) 0%, #f5f0fa 100%);
  min-height: 100vh;
  padding: 0 1rem 3rem;
}

.faq-page__header {
  text-align: center;
  padding: 3rem 1rem 1.5rem;
}

.faq-page__title {
  font-size: 2.2rem;
  background: linear-gradient(135deg, var(--hollywood-cerise), var(--violet-lush));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 0.5rem;
}

.faq-page__subtitle {
  font-size: 1.05rem;
  color: #666;
}

.faq-page__card {
  max-width: 750px;
  margin: 0 auto;
  padding: 2rem 2.5rem;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 20px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.06);
  animation: cardIn 0.5s ease both;
}

@keyframes cardIn {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
}

.faq-item {
  padding: 1.25rem 0;
  border-bottom: 1px solid rgba(228, 19, 147, 0.1);
}

.faq-item:last-child {
  border-bottom: none;
}

.faq-item--open {
  background: rgba(253, 247, 255, 0.5);
  margin: 0 -1.5rem;
  padding: 1.25rem 1.5rem;
  border-radius: 12px;
  border-bottom-color: transparent;
}

.faq-question {
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--hollywood-cerise);
  background: none;
  border: none;
  width: 100%;
  text-align: left;
  padding: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  cursor: pointer;
  transition: color 0.2s ease;
}

.faq-question:hover {
  color: var(--violet-lush);
}

.toggle-icon {
  font-size: 1.4rem;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(228, 19, 147, 0.08);
  color: var(--hollywood-cerise);
  transition: background-color 0.2s ease, transform 0.2s ease;
}

.faq-item--open .toggle-icon {
  background: var(--hollywood-cerise);
  color: white;
  transform: rotate(90deg);
}

.faq-answer {
  margin-top: 0.75rem;
  line-height: 1.8;
  color: #555;
  font-size: 0.97rem;
  text-align: left;
}

.faq-answer :deep(a) {
  color: var(--hollywood-cerise);
  text-decoration: none;
  font-weight: 500;
}

.faq-answer :deep(a:hover) {
  text-decoration: underline;
}

/* Slide transition */
.slide-enter-active,
.slide-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

@media (max-width: 768px) {
  .faq-page__title { font-size: 1.8rem; }
  .faq-page__card { padding: 1.25rem 1.5rem; border-radius: 16px; }
  .faq-item--open { margin: 0 -1rem; padding: 1rem; }
}
</style>
